import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const AuthContext = createContext({});

// Configuração do axios
axios.defaults.baseURL = 'https://lumione.com.br/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    // Inicializar e carregar dados se há token
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      // Validar token e carregar permissões
      validateToken();
    } else {
      setLoading(false);
    }
  }, []);

  const loadUserPermissions = async () => {
    try {
      const response = await axios.get('/users/profile');
      if (response.data.success && response.data.data.permissions) {
        setPermissions(response.data.data.permissions);
      }
    } catch (error) {
      console.error('Erro ao carregar permissões:', error);
      setPermissions({});
    }
  };

  const validateToken = async () => {
    try {
      const response = await axios.get('/auth/verify');
      if (response.data.success) {
        setUser(response.data.user);
        await loadUserPermissions();
      }
    } catch (error) {
      console.error('Token inválido:', error);
      // Limpar token inválido silenciosamente
      setUser(null);
      setToken(null);
      setPermissions({});
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setLoading(true);
      const response = await axios.post('/auth/login', {
        email,
        password
      });

      if (response.data.success) {
        const { token: newToken, user: userData } = response.data.data;
        
        setToken(newToken);
        setUser(userData);
        localStorage.setItem('token', newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        
        // Carregar permissões do usuário
        await loadUserPermissions();
        
        toast.success(`Bem-vindo(a), ${userData.nome || userData.email}!`);
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Erro ao fazer login';
      toast.error(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setPermissions({});
    localStorage.removeItem('token');
    localStorage.clear(); // Limpar todo o localStorage
    delete axios.defaults.headers.common['Authorization'];
    toast.info('Logout realizado com sucesso');
  };

  const cleanState = () => {
    setUser(null);
    setToken(null);
    setPermissions({});
    localStorage.removeItem('token');
    localStorage.clear();
    delete axios.defaults.headers.common['Authorization'];
  };

  // Função helper para verificar permissões (aceita tanto string "module:permission" quanto dois parâmetros)
  const hasPermission = (moduleOrFullPermission, permission = null) => {
    // Administradores sempre têm todas as permissões
    if (user?.role === 'admin') return true;
    
    let module, perm;
    
    if (permission === null && typeof moduleOrFullPermission === 'string' && moduleOrFullPermission.includes(':')) {
      // Formato "module:permission"
      [module, perm] = moduleOrFullPermission.split(':');
    } else {
      // Formato separado (module, permission)
      module = moduleOrFullPermission;
      perm = permission;
    }
    
    if (!permissions || !permissions[module]) {
      return false;
    }
    const scope = permissions[module][perm];
    return scope && scope !== 'none';
  };

  // Função helper para verificar se é admin
  const isAdmin = () => {
    return user?.role === 'admin';
  };

  // Função helper para obter escopo de permissão (aceita tanto string "module:permission" quanto dois parâmetros)
  const getPermissionScope = (moduleOrFullPermission, permission = null) => {
    // Administradores sempre têm escopo global
    if (user?.role === 'admin') return 'global';
    
    let module, perm;
    
    if (permission === null && typeof moduleOrFullPermission === 'string' && moduleOrFullPermission.includes(':')) {
      // Formato "module:permission"
      [module, perm] = moduleOrFullPermission.split(':');
    } else {
      // Formato separado (module, permission)
      module = moduleOrFullPermission;
      perm = permission;
    }
    
    if (!permissions || !permissions[module]) return 'none';
    return permissions[module][perm] || 'none';
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await axios.post('/auth/register', userData);
      
      if (response.data.success) {
        toast.success('Usuário registrado com sucesso!');
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Erro ao registrar usuário';
      toast.error(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    token,
    loading,
    permissions,
    login,
    logout,
    register,
    cleanState,
    hasPermission,
    isAdmin,
    getPermissionScope,
    isAuthenticated: !!user && !!token
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};